"use client";

import { useMemo } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurveChart } from "@/components/tools/astronomy/curve-chart";
import type { DayLengthStrings } from "@/components/tools/day-length-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import {
  monthStarts,
  signedMinutesSeconds,
  yearDaylight,
} from "@/lib/tool-kit/astronomy/daylight";
import {
  clockWithDay,
  hoursAndMinutes,
  longDay,
} from "@/lib/tool-kit/astronomy/format";
import { solarDayOnLocalDate } from "@/lib/tool-kit/astronomy/solar";
import { dayNumber, localDayNumber } from "@/lib/tool-kit/astronomy/zone";

interface Props {
  date: { year: number; month: number; day: number };
  form: { place: string; zone: string; date: string };
  latitude: number;
  longitude: number;
  locale: string;
  strings: DayLengthStrings;
}

function YearChart({
  props,
  days,
  yearIndex,
  hours,
}: {
  props: Props;
  days: readonly { daylightSeconds: number }[];
  yearIndex: number;
  hours: number;
}) {
  const { date, locale, strings } = props;
  const points = days.map(
    (entry, index) => [index, entry.daylightSeconds / 3600] as const
  );
  const monthLabel = (month: number) =>
    formatDateTime(locale, new Date(Date.UTC(2000, month, 1)), {
      month: "short",
      timeZone: "UTC",
    });
  return (
    <CurveChart
      description={strings.chartDescription}
      id="dl-chart"
      label={strings.chartLabel}
      marker={{ x: yearIndex, y: hours }}
      points={points}
      xDomain={[0, points.length - 1]}
      xTicks={monthStarts(date.year).map((value, month) => ({
        value,
        label: monthLabel(month),
      }))}
      xTitle={strings.chartX}
      yDomain={[0, 24]}
      yTicks={[0, 4, 8, 12, 16, 20, 24].map((value) => ({
        value,
        label: String(value),
      }))}
      yTitle={strings.chartY}
    />
  );
}

export function DayLengthResults(props: Props) {
  const { date, form, latitude, longitude, locale, strings } = props;
  const fmt = useToolNumber();
  const localDay = dayNumber(date.year, date.month, date.day);
  const zoneDay = (ms: number) => localDayNumber(ms, form.zone);
  const sun = solarDayOnLocalDate(latitude, longitude, localDay, zoneDay);
  const before = solarDayOnLocalDate(
    latitude,
    longitude,
    localDay - 1,
    zoneDay
  );
  const summary = useMemo(
    () => yearDaylight(latitude, longitude, date.year),
    [latitude, longitude, date.year]
  );
  const clock = (ms: number) =>
    clockWithDay(locale, ms, form.zone, localDay, strings);
  const length = hoursAndMinutes(sun.daylightSeconds);
  const lengthText = t(strings.duration, {
    hours: fmt(length.hours),
    minutes: fmt(length.minutes),
  });
  const step = signedMinutesSeconds(
    sun.daylightSeconds - before.daylightSeconds
  );
  const bothNormal = sun.status === "normal" && before.status === "normal";
  const yearStart = dayNumber(date.year, 1, 1);
  const describeYearDay = (entry: { day: number; daylightSeconds: number }) => {
    const span = hoursAndMinutes(entry.daylightSeconds);
    return t(strings.dayAndLength, {
      date: formatDateTime(locale, new Date(entry.day * 86_400_000), {
        timeZone: "UTC",
        day: "numeric",
        month: "long",
      }),
      hours: fmt(span.hours),
      minutes: fmt(span.minutes),
    });
  };
  const place =
    (strings.placeNames as Record<string, string>)[form.place] ??
    `${fmt(latitude)}, ${fmt(longitude)}`;
  const longDate = longDay(locale, localDay);
  const summaryText = t(strings.summary, {
    place,
    date: longDate,
    sunrise: sun.sunrise === null ? "—" : clock(sun.sunrise),
    sunset: sun.sunset === null ? "—" : clock(sun.sunset),
    hours: fmt(length.hours),
    minutes: fmt(length.minutes),
  });
  return (
    <>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{t(strings.results, { date: longDate })}</h2>
        <StatGrid>
          <StatCard emphasis label={strings.daylight} value={lengthText} />
          <StatCard
            label={strings.sunrise}
            value={sun.sunrise === null ? "—" : clock(sun.sunrise)}
          />
          <StatCard
            label={strings.sunset}
            value={sun.sunset === null ? "—" : clock(sun.sunset)}
          />
          <StatCard label={strings.solarNoon} value={clock(sun.noon)} />
          <StatCard
            label={strings.decimalHours}
            value={t(strings.hoursValue, {
              value: fmt(sun.daylightSeconds / 3600, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            })}
          />
          <StatCard
            label={strings.change}
            value={
              bothNormal
                ? t(strings.changeValue, {
                    sign: step.sign,
                    minutes: fmt(step.minutes),
                    seconds: fmt(step.seconds),
                  })
                : "—"
            }
          />
        </StatGrid>
        {sun.status === "polar-day" ? (
          <Notice>{strings.polarDay}</Notice>
        ) : null}
        {sun.status === "polar-night" ? (
          <Notice>{strings.polarNight}</Notice>
        ) : null}
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={summaryText}
        />
      </section>
      <section className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>
          {t(strings.yearHeading, { year: String(date.year) })}
        </h3>
        <StatGrid>
          <StatCard
            label={t(strings.longest, { year: String(date.year) })}
            value={describeYearDay(summary.longest)}
          />
          <StatCard
            label={t(strings.shortest, { year: String(date.year) })}
            value={describeYearDay(summary.shortest)}
          />
        </StatGrid>
        {summary.polarDays + summary.polarNights > 0 ? (
          <Notice>
            {t(strings.polarYear, {
              days: fmt(summary.polarDays),
              nights: fmt(summary.polarNights),
              year: String(date.year),
            })}
          </Notice>
        ) : null}
        <YearChart
          days={summary.days}
          hours={sun.daylightSeconds / 3600}
          props={props}
          yearIndex={localDay - yearStart}
        />
      </section>
    </>
  );
}
